import { type ReactElement, startTransition } from "react"

import { AnimatePresence, motion } from "framer-motion"
import { useBoolean } from "react-use"
import sanitizeHtml from "sanitize-html"

import { type TranslationObj } from "$/utils/lang"

import { ETypographyColor, ETypographySize, ETypographyTag, Typography } from "@/3514/components"
import { buildCssClassName, getTransitionTwClasses } from "@/3514/utils"
import {
    type IUseTranslation,
    type TGetValueFromTranslatedObjectFunction,
    useGetValueFromTranslatedObject,
    useTranslation
} from "@/hooks"

import { WarningMessage } from "../WarningMessage"

import "./styles.scss"

type Props = {
    errors: any
    item: any
    error?: string | null
    name?: string
    register: any
    questionNumber: number
    questionStyle?: "one-line" | "brand"
    sampleResponse?: TranslationObj | null
}

const classes = {
    sampleCtn: ({ isOpen }: { isOpen: boolean }): string =>
        buildCssClassName(
            "py-[5px] px-[10px] rounded-[5px]",
            { "bg-white": !isOpen, "bg-blue-500/40": isOpen },
            getTransitionTwClasses("all", 200)
        ),
    samplerBtn: ({ isOpen }: { isOpen: boolean }): string =>
        buildCssClassName(
            "border-transparent",
            "border-[1px]",
            "rounded-[5px]",
            "!outline-none",
            "text-[12px]",
            "leading-[18px]",
            "font-[300]",
            "decoration-[1px]",
            "underline",
            "focus-visible:text-gray-200",
            "focus-visible:border-gray-23",
            "focus-visible:decoration-transparent",
            isOpen
                ? ["text-gray-200", "underline-offset-[5px]", "decoration-gray-200"]
                : [
                      "hover:text-gray-200",
                      "hover:underline-offset-[5px]",
                      "hover:decoration-gray-200",
                      "underline-offset-[3px]",
                      "decoration-gray-23",
                      "text-gray-23"
                  ],
            getTransitionTwClasses("all", 200)
        ),
    samplerTxt: (): string => "mt-2 mb-0 [white-space:pre-line]"
}

function sanitizePlain(text: string): string {
    return sanitizeHtml(text, { allowedTags: [] })
}

const FreeResponseComponent = ({
    errors,
    item,
    register,
    questionNumber,
    error,
    name,
    questionStyle = "brand",
    sampleResponse
}: Props): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const [isPreviewOpen, setIsPreviewOpen]: ReturnType<typeof useBoolean> = useBoolean(false)

    const getValueFromTranslatedObject: TGetValueFromTranslatedObjectFunction = useGetValueFromTranslatedObject()

    const sampleResponseText: string = sampleResponse
        ? sanitizePlain(getValueFromTranslatedObject(sampleResponse) || "")
        : null

    return (
        <>
            {questionStyle === "brand" && (
                <h4 className="h5 color-brand text-uppercase font-extrabold title-underline mb-3">
                    <span>{questionNumber}</span>
                </h4>
            )}
            <WarningMessage message={error || errors?.[item.uuid]?.message?.toString()} />
            <div className="flex flex-col gap-[5px] mb-[50px] last:mb-0">
                <div className="card br-5 card-offboarding">
                    <div className="card-header br-5 border-bottom-0 px-20 py-3 card-header-offboarding">
                        {questionStyle === "one-line" && (
                            <p className="m-0">
                                <span className="font-extrabold">{questionNumber}. </span>
                                <span
                                    className="d-inline"
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizePlain(getValueFromTranslatedObject(item?.question))
                                    }}
                                />
                            </p>
                        )}
                    </div>
                    <textarea
                        className="type-text-field"
                        aria-label={t("Start typing...")}
                        data-testid="free-response-text-area"
                        {...register(name ?? item.uuid)}
                        placeholder={sanitizePlain(getValueFromTranslatedObject(item.prompt) || t("Start typing..."))}
                    />
                </div>

                {sampleResponseText && (
                    <div className={classes.sampleCtn({ isOpen: isPreviewOpen })}>
                        <button
                            type="button"
                            className={classes.samplerBtn({ isOpen: isPreviewOpen })}
                            aria-expanded={isPreviewOpen}
                            aria-label={t("participantSide.survey.freeResponse.sampler.textHolder.ariaLabel")}
                            aria-controls="sample-response-body"
                            onClick={(): void =>
                                startTransition((): void => setIsPreviewOpen((prev: boolean): boolean => !prev))
                            }
                        >
                            <Typography
                                text={
                                    isPreviewOpen
                                        ? t("participantSide.survey.freeResponse.sampler.opened.title")
                                        : t("participantSide.survey.freeResponse.sampler.closed.title")
                                }
                                size={ETypographySize.Tiny}
                                tag={ETypographyTag.Paragraph}
                                color={ETypographyColor.Gray23}
                            />
                        </button>
                        <AnimatePresence initial={false}>
                            {isPreviewOpen && (
                                <motion.div
                                    id="sample-response-body"
                                    role="region"
                                    aria-label={t("participantSide.survey.freeResponse.sampler.textHolder.ariaLabel")}
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.22, ease: "easeInOut" }}
                                    style={{ overflow: "hidden" }}
                                >
                                    <Typography
                                        className={classes.samplerTxt()}
                                        text={sampleResponseText}
                                        tag={ETypographyTag.Paragraph}
                                        color={ETypographyColor.Dark}
                                        size={ETypographySize.ExtraSmall}
                                        withHyphens
                                        isPretty
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </>
    )
}

export default FreeResponseComponent
